import { GlossaryBlueprint } from "../../types-games";
import { sectionDataObject } from "../stores/objectsStore";
import { parseBlueprintData } from "./blueprintDataParser";
import { extractBlueprintVersion, versionsMatch } from "./versionUtils";

export interface BlueprintVersionChanges {
  hasChanges: boolean;
  addedSections: string[];
  removedSections: string[];
  modifiedSections: string[];
  addedFields: { sectionId: string; fieldId: string; fieldLabel: string }[];
  removedFields: { sectionId: string; fieldId: string; fieldLabel: string }[];
  modifiedFields: { sectionId: string; fieldId: string; fieldLabel: string; changes: string[] }[];
}

/**
 * Compare two blueprint versions and detect specific changes
 * @param currentBlueprint - The current blueprint
 * @param previousBlueprintVersion - The version number that the glossary item was created with
 * @param glossaryItemSections - The sections from the glossary item
 */
export function detectBlueprintChanges(
  currentBlueprint: GlossaryBlueprint,
  previousBlueprintVersion: number | { major: number; minor: number; patch: number } | undefined,
  glossaryItemSections: sectionDataObject[]
): BlueprintVersionChanges {
  const changes: BlueprintVersionChanges = {
    hasChanges: false,
    addedSections: [],
    removedSections: [],
    modifiedSections: [],
    addedFields: [],
    removedFields: [],
    modifiedFields: []
  };

  // Extract comparable version numbers
  const currentVersion = extractBlueprintVersion(currentBlueprint);
  
  // Convert previousBlueprintVersion to comparable number
  let previousVersion: number | undefined;
  if (typeof previousBlueprintVersion === 'number') {
    previousVersion = previousBlueprintVersion;
  } else if (previousBlueprintVersion && typeof previousBlueprintVersion === 'object' && 'major' in previousBlueprintVersion) {
    // Convert major.minor.patch to comparable number
    previousVersion = previousBlueprintVersion.major * 1000000 + previousBlueprintVersion.minor * 1000 + previousBlueprintVersion.patch;
  }

  // If versions match, no changes
  if (versionsMatch(currentVersion, previousVersion)) {
    return changes;
  }

  changes.hasChanges = true;

  // Create maps for easy comparison
  const currentSectionsMap = new Map<string, any>();
  const currentFieldsMap = new Map<string, any>();
  const existingSectionsMap = new Map<string, sectionDataObject>();
  const existingFieldsMap = new Map<string, any>();

  // Parse current blueprint structure
  const parsedCurrentBlueprint = parseBlueprintData(currentBlueprint);
  
  // Build current blueprint structure map
  if (parsedCurrentBlueprint.sections) {
    parsedCurrentBlueprint.sections.forEach(section => {
      currentSectionsMap.set(section.id, section);
      section.fieldIds.forEach(fieldId => {
        const field = parsedCurrentBlueprint.fields.find(f => f.id === fieldId);
        if (field) {
          currentFieldsMap.set(fieldId, { ...field, sectionId: section.id });
        }
      });
    });
  }

  // Build existing (glossary item) structure map
  glossaryItemSections.forEach(section => {
    existingSectionsMap.set(section.id, section);
    section.fields.forEach(field => {
      existingFieldsMap.set(field.id, { ...field, sectionId: section.id });
    });
  });

  // Check for added sections (in current blueprint but not in existing item)
  currentSectionsMap.forEach((section, sectionId) => {
    if (!existingSectionsMap.has(sectionId)) {
      changes.addedSections.push(sectionId);
    }
  });

  // Check for removed sections (in existing item but not in current blueprint)
  existingSectionsMap.forEach((section, sectionId) => {
    if (!currentSectionsMap.has(sectionId)) {
      changes.removedSections.push(sectionId);
    }
  });

  // Check for added fields
  currentFieldsMap.forEach((field, fieldId) => {
    if (!existingFieldsMap.has(fieldId)) {
      changes.addedFields.push({
        sectionId: field.sectionId,
        fieldId: fieldId,
        fieldLabel: field.label
      });
    }
  });

  // Check for removed fields
  existingFieldsMap.forEach((field, fieldId) => {
    if (!currentFieldsMap.has(fieldId)) {
      changes.removedFields.push({
        sectionId: field.sectionId,
        fieldId: fieldId,
        fieldLabel: field.label
      });
    }
  });

  // Check for modified fields (same ID but different properties)
  currentFieldsMap.forEach((currentField, fieldId) => {
    const existingField = existingFieldsMap.get(fieldId);
    if (existingField) {
      const fieldChanges = detectFieldChanges(currentField, existingField);
      if (fieldChanges.length > 0) {
        changes.modifiedFields.push({
          sectionId: currentField.sectionId,
          fieldId: fieldId,
          fieldLabel: currentField.label,
          changes: fieldChanges
        });
      }
    }
  });

  // Determine which sections have been modified
  const modifiedSectionIds = new Set<string>();
  
  // Add sections that have added or removed fields
  changes.addedFields.forEach(field => modifiedSectionIds.add(field.sectionId));
  changes.removedFields.forEach(field => modifiedSectionIds.add(field.sectionId));
  changes.modifiedFields.forEach(field => modifiedSectionIds.add(field.sectionId));

  changes.modifiedSections = Array.from(modifiedSectionIds);

  return changes;
}

/**
 * Detect changes between two field configurations
 */
function detectFieldChanges(currentField: any, existingField: any): string[] {
  const fieldChanges: string[] = [];

  // Check basic properties
  if (currentField.type !== existingField.type) {
    fieldChanges.push(`Type changed from ${existingField.type} to ${currentField.type}`);
  }

  if (currentField.label !== existingField.label) {
    fieldChanges.push(`Label changed from "${existingField.label}" to "${currentField.label}"`);
  }

  if (currentField.required !== existingField.required) {
    fieldChanges.push(`Required changed from ${existingField.required} to ${currentField.required}`);
  }

  if (currentField.multiple !== existingField.multiple) {
    fieldChanges.push(`Multiple changed from ${existingField.multiple} to ${currentField.multiple}`);
  }

  if (currentField.translation !== existingField.translation) {
    fieldChanges.push(`Translation changed from ${existingField.translation} to ${currentField.translation}`);
  }

  if (currentField.filter !== existingField.filter) {
    fieldChanges.push(`Filter changed from ${existingField.filter} to ${currentField.filter}`);
  }

  // Check filter options
  const currentFilterOptions = JSON.stringify(currentField.filterOptions || []);
  const existingFilterOptions = JSON.stringify(existingField.filterOptions || []);
  if (currentFilterOptions !== existingFilterOptions) {
    fieldChanges.push('Filter options changed');
  }

  return fieldChanges;
}

/**
 * Check if a section has any changes
 */
export function sectionHasChanges(sectionId: string, changes: BlueprintVersionChanges): boolean {
  return changes.addedSections.includes(sectionId) ||
         changes.removedSections.includes(sectionId) ||
         changes.modifiedSections.includes(sectionId);
}

/**
 * Check if a field has any changes
 */
export function fieldHasChanges(fieldId: string, changes: BlueprintVersionChanges): boolean {
  return changes.addedFields.some(f => f.fieldId === fieldId) ||
         changes.removedFields.some(f => f.fieldId === fieldId) ||
         changes.modifiedFields.some(f => f.fieldId === fieldId);
}

/**
 * Apply current blueprint properties to existing glossary item sections
 * This ensures that fields use the latest blueprint definitions (filters, types, etc.)
 */
export function applyBlueprintPropertiesToSections(
  existingSections: sectionDataObject[],
  currentBlueprint: GlossaryBlueprint
): sectionDataObject[] {
  // Parse the current blueprint to get the latest field definitions
  const parsedBlueprint = parseBlueprintData(currentBlueprint);
  
  // Create a map of field IDs to their current blueprint definitions
  const currentFieldsMap = new Map<string, any>();
  parsedBlueprint.fields.forEach(field => {
    currentFieldsMap.set(field.id, field);
  });

  // Update existing sections with current blueprint properties
  return existingSections.map(section => ({
    ...section,
    fields: section.fields.map(field => {
      const currentFieldDef = currentFieldsMap.get(field.id);
      
      if (currentFieldDef) {
        // Merge the current blueprint properties while preserving the existing value
        return {
          ...field,
          type: currentFieldDef.type,
          label: currentFieldDef.label,
          required: currentFieldDef.required || false,
          multiple: currentFieldDef.multiple || false,
          translation: currentFieldDef.translation || false,
          filter: currentFieldDef.filter || false,
          filterOptions: currentFieldDef.filterOptions || [],
          // Preserve the existing value and string_id
          value: field.value,
          string_id: field.string_id,
        };
      }
      
      // Return field unchanged if no blueprint definition found
      return field;
    })
  }));
}

/**
 * Add new fields from blueprint to existing sections
 */
export function addNewFieldsToSections(
  existingSections: sectionDataObject[],
  currentBlueprint: GlossaryBlueprint,
  changes: BlueprintVersionChanges
): sectionDataObject[] {
  if (changes.addedFields.length === 0) {
    return existingSections;
  }

  // Parse the current blueprint to get the latest field definitions
  const parsedBlueprint = parseBlueprintData(currentBlueprint);
  
  // Create a map of field IDs to their current blueprint definitions
  const currentFieldsMap = new Map<string, any>();
  parsedBlueprint.fields.forEach(field => {
    currentFieldsMap.set(field.id, field);
  });

  // Group added fields by section
  const addedFieldsBySection = new Map<string, any[]>();
  changes.addedFields.forEach(addedField => {
    const fieldDef = currentFieldsMap.get(addedField.fieldId);
    if (fieldDef) {
      if (!addedFieldsBySection.has(addedField.sectionId)) {
        addedFieldsBySection.set(addedField.sectionId, []);
      }
      addedFieldsBySection.get(addedField.sectionId)!.push({
        id: fieldDef.id,
        type: fieldDef.type,
        label: fieldDef.label,
        value: fieldDef.defaultValue || '',
        required: fieldDef.required || false,
        multiple: fieldDef.multiple || false,
        translation: fieldDef.translation || false,
        filter: fieldDef.filter || false,
        filterOptions: fieldDef.filterOptions || [],
      });
    }
  });

  // Add new fields to existing sections
  return existingSections.map(section => {
    const newFields = addedFieldsBySection.get(section.id) || [];
    if (newFields.length > 0) {
      return {
        ...section,
        fields: [...section.fields, ...newFields]
      };
    }
    return section;
  });
}

/**
 * Get a summary of changes for display
 */
export function getChangeSummary(changes: BlueprintVersionChanges): string {
  if (!changes.hasChanges) {
    return "No changes detected";
  }

  const summaryParts: string[] = [];

  if (changes.addedSections.length > 0) {
    summaryParts.push(`${changes.addedSections.length} section(s) added`);
  }

  if (changes.removedSections.length > 0) {
    summaryParts.push(`${changes.removedSections.length} section(s) removed`);
  }

  if (changes.addedFields.length > 0) {
    summaryParts.push(`${changes.addedFields.length} field(s) added`);
  }

  if (changes.removedFields.length > 0) {
    summaryParts.push(`${changes.removedFields.length} field(s) removed`);
  }

  if (changes.modifiedFields.length > 0) {
    summaryParts.push(`${changes.modifiedFields.length} field(s) modified`);
  }

  return summaryParts.join(', ');
}
